import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { arrayMove } from "@dnd-kit/sortable";
import type { Column } from "./types";

const DEFAULT_RESIZE_WIDTH = 120;

function readCellWidthPx(el: HTMLElement | null): number {
  if (!el) return DEFAULT_RESIZE_WIDTH;
  const w = el.offsetWidth;
  return w > 0 ? w : DEFAULT_RESIZE_WIDTH;
}

export type UseTableColumnLayoutParams<T extends object> = {
  columnsRaw: Column<T>[];
  columnResize: boolean;
  columnReorder: boolean;
  columnWidths?: Record<string, number>;
  columnVisibility?: Record<string, boolean>;
  onColumnWidthsChange?: (widths: Record<string, number>) => void;
  onColumnWidthsEnd?: (widths: Record<string, number>) => void;
  onColumnCheckboxEnd?: (checked: Record<string, boolean>) => void;
  columnOrder?: string[];
  onColumnOrderChange?: (order: string[]) => void;
  onColumnVisibilityChange?: (key: string, visible: boolean) => void;
};

export type UseTableColumnLayoutResult<T extends object> = {
  columnsWithWidths: Column<T>[];
  effectiveFlexibleOrder: string[];
  allFlexibleKeys: string[];
  widths: Record<string, number>;
  sensors: ReturnType<typeof useSensors>;
  columnVisibility: Record<string, boolean>;
  onColumnVisibilityChange: (key: string, visible: boolean) => void;
  onColumnDragEnd: (event: DragEndEvent) => void;
  onResizeStart: (
    e: React.PointerEvent,
    colKey: string,
    cellEl: HTMLElement | null,
  ) => void;
  widthResizing: Record<string, number>;
};

export function useTableColumnLayout<T extends object>(
  params: UseTableColumnLayoutParams<T>,
): UseTableColumnLayoutResult<T> {
  const {
    columnsRaw,
    columnResize,
    columnReorder,
    columnWidths: columnWidthsProp,
    onColumnWidthsChange,
    onColumnWidthsEnd,
    onColumnCheckboxEnd,
    columnOrder: columnOrderProp,
    onColumnOrderChange,
    columnVisibility: columnVisibilityProp,
    onColumnVisibilityChange: onColumnVisibilityChangeProp,
  } = params;

  const [internalWidths, setInternalWidths] = useState<Record<string, number>>(
    {},
  );
  const [internalFlexibleOrder, setInternalFlexibleOrder] = useState<string[]>(
    [],
  );

  const [internalColumnVisibility, setInternalColumnVisibility] = useState<
    Record<string, boolean>
  >({});

  const isWidthsControlled = columnWidthsProp !== undefined;
  const widths =
    columnWidthsProp !== undefined ? columnWidthsProp : internalWidths;

  const widthsRef = useRef(widths);
  useEffect(() => {
    widthsRef.current = widths;
  }, [widths]);

  const isOrderControlled = columnOrderProp !== undefined;
  const flexibleOrderState =
    columnOrderProp !== undefined ? columnOrderProp : internalFlexibleOrder;

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 6 },
    }),
  );

  const fixedLeftKeys = useMemo(
    () => columnsRaw.filter((c) => c.fixed === "left").map((c) => c.key),
    [columnsRaw],
  );

  const allFlexibleKeys = useMemo(
    () => columnsRaw.filter((c) => c.fixed !== "left").map((c) => c.key),
    [columnsRaw],
  );

  const flexibleKeysSig = allFlexibleKeys.join("\0");

  const columnVisibility = useMemo(() => {
    const initialVisibility = Object.fromEntries(
      columnsRaw.map((col) => [col.key, col.checkbox ?? true]),
    );

    return {
      ...initialVisibility,
      // ...(columnVisibilityProp ?? internalColumnVisibility),
    };
  }, [columnsRaw, columnVisibilityProp, internalColumnVisibility]);

  const handleColumnVisibilityChange = useCallback(
    (key: string, visible: boolean) => {
      const next = {
        ...columnVisibility,
        [key]: visible,
      };

      if (columnVisibilityProp !== undefined) {
        onColumnVisibilityChangeProp?.(key, visible);
      } else {
        setInternalColumnVisibility(next);
      }

      onColumnCheckboxEnd?.(next);
    },
    [
      columnVisibility,
      columnVisibilityProp,
      onColumnVisibilityChangeProp,
      onColumnCheckboxEnd,
    ],
  );

  useEffect(() => {
    if (isOrderControlled) return;
    setInternalFlexibleOrder((prev) => {
      const kept = prev.filter((k) => allFlexibleKeys.includes(k));
      const added = allFlexibleKeys.filter((k) => !kept.includes(k));
      return [...kept, ...added];
    });
  }, [flexibleKeysSig, allFlexibleKeys, isOrderControlled]);

  const effectiveFlexibleOrder = useMemo(() => {
    const o = flexibleOrderState.filter((k) => allFlexibleKeys.includes(k));
    const missing = allFlexibleKeys.filter((k) => !o.includes(k));
    return o.length === 0 && missing.length > 0
      ? [...missing]
      : [...o, ...missing];
  }, [flexibleOrderState, allFlexibleKeys]);

  const columnsOrderedRaw = useMemo(() => {
    const fixed = fixedLeftKeys
      .map((k) => columnsRaw.find((c) => c.key === k))
      .filter(Boolean) as Column<T>[];
    const flexMap = Object.fromEntries(
      columnsRaw.filter((c) => c.fixed !== "left").map((c) => [c.key, c]),
    );

    const flexible = effectiveFlexibleOrder
      .map((k) => flexMap[k])
      .filter(Boolean) as Column<T>[];
    return [...fixed, ...flexible];
  }, [columnsRaw, fixedLeftKeys, effectiveFlexibleOrder]);

  const columnsWithWidths = useMemo(
    () =>
      columnsOrderedRaw.map((col) => ({
        ...col,
        width: widths[col.key] ?? col.width,
      })),
    [columnsOrderedRaw, widths],
  );

  const setWidthForKey = useCallback(
    (key: string, w: number) => {
      const next = { ...widthsRef.current, [key]: w };
      widthsRef.current = next;
      if (isWidthsControlled) onColumnWidthsChange?.(next);
      else setInternalWidths(next);
    },
    [isWidthsControlled, onColumnWidthsChange],
  );

  const resizeSession = useRef<{
    key: string;
    startX: number;
    startWidth: number;
    min: number;
    max: number;
  } | null>(null);

  const onResizeStart = useCallback(
    (e: React.PointerEvent, colKey: string, cellEl: HTMLElement | null) => {
      if (!columnResize) return;
      const col = columnsOrderedRaw.find((c) => c.key === colKey);
      if (!col) return;
      (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
      const min = typeof col.minWidth === "number" ? col.minWidth : 48;
      const max = typeof col.maxWidth === "number" ? col.maxWidth : 2000;
      let startWidth: number;
      if (typeof col.width === "number") {
        startWidth = col.width;
      } else {
        startWidth = readCellWidthPx(cellEl);
        setWidthForKey(colKey, startWidth);
      }
      resizeSession.current = {
        key: colKey,
        startX: e.clientX,
        startWidth,
        min,
        max,
      };
    },
    [columnResize, columnsOrderedRaw, setWidthForKey],
  );

  useEffect(() => {
    if (!columnResize) return;

    const onMove = (e: PointerEvent) => {
      const s = resizeSession.current;
      if (!s) return;
      const dx = e.clientX - s.startX;
      const next = Math.round(
        Math.min(s.max, Math.max(s.min, s.startWidth + dx)),
      );
      setWidthForKey(s.key, next);
    };

    const onUp = () => {
      const hadSession = resizeSession.current != null;
      resizeSession.current = null;
      if (hadSession) onColumnWidthsEnd?.(widthsRef.current);
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };
  }, [columnResize, setWidthForKey, onColumnWidthsEnd]);

  useEffect(() => {
    // onColumnCheckboxEnd?.(internalColumnVisibility);
  }, [internalColumnVisibility, onColumnCheckboxEnd]);

  const onColumnDragEnd = useCallback(
    (event: DragEndEvent) => {
      if (!columnReorder) return;
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const oldIndex = effectiveFlexibleOrder.indexOf(String(active.id));
      const newIndex = effectiveFlexibleOrder.indexOf(String(over.id));
      if (oldIndex < 0 || newIndex < 0) return;
      const next = arrayMove(effectiveFlexibleOrder, oldIndex, newIndex);
      if (isOrderControlled) onColumnOrderChange?.(next);
      else setInternalFlexibleOrder(next);
    },
    [
      columnReorder,
      effectiveFlexibleOrder,
      isOrderControlled,
      onColumnOrderChange,
    ],
  );

  return {
    columnsWithWidths,
    effectiveFlexibleOrder,
    allFlexibleKeys,
    widths,
    sensors,
    onColumnDragEnd,
    onResizeStart,
    widthResizing: internalWidths,
    columnVisibility,
    onColumnVisibilityChange: handleColumnVisibilityChange,
  };
}
